import LineChart from '@cloudscape-design/components/line-chart';
import { Box, Spinner } from '@cloudscape-design/components';
import { useTranslation } from 'react-i18next';
import { colours } from "./colours";

const getRenderOrder = (key) => {
  if (key === 'Interruptions') return 1;
  else if (key === 'Positive') return 2;
  else if (key === 'Negative') return 1;
  else if (key === 'Neutral') return 3;
  else if (key.indexOf('Customer') >= 0) return 5;
  else return 10;
}

export const ComprehendSentimentChart = ({ comprehendSentimentData, speakerLabels, chartHeight = 237 }) => {
  const { t } = useTranslation();

  if (comprehendSentimentData === undefined) {
    return <Spinner />;
  }

  // Transformar datos al formato que espera Cloudscape LineChart
  // Los datos vienen como: { "spk_0": [{x: 0, y: 1.2}, {x: 1, y: 0.5}, ...], ... }
  
  // Crear las series de líneas, filtrando Positive/Negative/Neutral y solo incluyendo speakers (spk_*)
  const series = Object.keys(speakerLabels)
    .filter(key => {
      const label = speakerLabels[key];
      // Filtrar Positive, Negative, Neutral, Interruptions, NonTalkTime
      if (label.includes('Positive') || 
          label.includes('Negative') || 
          label.includes('Neutral') ||
          label === 'Interruptions' ||
          label === 'NonTalkTime' ||
          label === 'Silence') {
        return false;
      }
      // Solo incluir si tiene datos
      return key in comprehendSentimentData && 
             Array.isArray(comprehendSentimentData[key]) && 
             comprehendSentimentData[key].length > 0;
    })
    .map(key => ({
      type: 'line',
      title: speakerLabels[key],
      data: comprehendSentimentData[key].map(point => ({
        x: point.x,
        y: point.y
      })),
      color: colours[key] || colours[`spk_${key}`] || '#5351FB',
      valueFormatter: (value) => value.toFixed(2)
    }))
    .sort((a, b) => {
      const orderA = getRenderOrder(a.title);
      const orderB = getRenderOrder(b.title);
      return orderA - orderB;
    });

  // Calcular el dominio del eje X (segundos)
  const allXValues = [];
  series.forEach(s => {
    s.data.forEach(point => {
      if (typeof point.x === 'number' && !allXValues.includes(point.x)) {
        allXValues.push(point.x);
      }
    });
  });
  const maxX = allXValues.length > 0 ? Math.max(...allXValues) : 0;

  // Calcular el promedio del sentimiento
  let totalSum = 0;
  let totalCount = 0;
  series.forEach(s => {
    s.data.forEach(point => {
      if (typeof point.y === 'number' && !isNaN(point.y)) {
        totalSum += point.y;
        totalCount++;
      }
    });
  });
  const averageSentiment = totalCount > 0 ? totalSum / totalCount : 0;

  if (series.length === 0) {
    return (
      <Box textAlign="center" padding="l">
        <b>{t("charts.noData")}</b>
      </Box>
    );
  }

  // Crear serie threshold para el promedio
  const thresholdSeries = {
    type: 'threshold',
    title: t("charts.averageSentiment") || "Promedio",
    y: averageSentiment,
    color: '#999'
  };

  // Combinar series de líneas con threshold
  const allSeries = [...series, thresholdSeries];

  return (
    <LineChart
      series={allSeries}
      height={chartHeight}
      xScaleType="linear"
      yScaleType="linear"
      loadingText='cargando...'
      xDomain={[0, maxX]}
      yDomain={[-5, 5]}
      xTitle={t("charts.seconds") || "Segundos"}
      yTitle={t("charts.sentiment") || "Sentimiento"}
      emphasizeBaselineAxis={true}
      hideFilter={true}
      ariaLabel={t("charts.sentimentChartAriaLabel") || "Gráfico de sentimiento por segundo"}
      ariaDescription={t("charts.sentimentChartDescription") || "Muestra la evolución del sentimiento de cada participante a lo largo del tiempo"}
      i18nStrings={{
        legendAriaLabel: t("charts.legendAriaLabel") || "Leyenda",
        chartAriaRoleDescription: t("charts.chartAriaRoleDescription") || "gráfico de líneas",
        xAxisAriaRoleDescription: t("charts.xAxisAriaRoleDescription") || "eje X",
        yAxisAriaRoleDescription: t("charts.yAxisAriaRoleDescription") || "eje Y"
      }}
    />
  );
};
